import { newId, type DataSource, type NumberNodeProject } from '@numbernode/contracts';
import { getProjectFile, listProjects, loadProject, putProjectFile, saveProject } from '@numbernode/project-storage';
import { importIntoEngine, initialiseEngine } from '../engine/client';
import { safeTableName, normaliseFile } from './files';
import { useProjectStore } from '../store/projectStore';

function semanticType(type: string, name: string): 'dimension' | 'measure' | 'date' | 'geography' | 'identifier' {
  if (/DATE|TIME/i.test(type)) return 'date';
  if (/lat|latitude|lon|lng|longitude|country|city|state|postcode|zip/i.test(name)) return 'geography';
  if (/(^id$|_id$|key$)/i.test(name)) return 'identifier';
  if (/INT|DECIMAL|DOUBLE|FLOAT|REAL|HUGEINT|UBIGINT/i.test(type)) return 'measure';
  return 'dimension';
}

export async function importDataFile(file: File): Promise<DataSource> {
  const store = useProjectStore.getState();
  const normalised = await normaliseFile(file);
  const tableName = safeTableName(file.name, store.project.sources.map((source) => source.tableName));
  const table = await importIntoEngine(normalised.fileName, tableName, normalised.format, normalised.bytes);
  const storageKey = `data/${newId('source')}-${normalised.fileName}`;
  await putProjectFile(store.project.id, storageKey, normalised.bytes, normalised.mime);
  const source: DataSource = {
    id: newId('source'), name: file.name.replace(/\.[^.]+$/,''), tableName, fileName: normalised.fileName,
    format: normalised.format as DataSource['format'], size: normalised.bytes.byteLength, storageKey,
    importedAt: new Date().toISOString(), rowCount: table.rowCount,
    columns: table.columns.map((column) => ({ name: column.name, physicalType: column.type, semanticType: semanticType(column.type, column.name), nullable: column.nullable, hidden: false })),
  };
  store.mutate(`Import ${file.name}`, (project) => { project.sources.push(source); });
  useProjectStore.getState().setSelectedSource(source.id);
  return source;
}

export async function rehydrateProject(project: NumberNodeProject): Promise<void> {
  await initialiseEngine();
  for (const source of project.sources) {
    if (source.format === 'view') continue;
    const bytes = await getProjectFile(project.id, source.storageKey);
    if (bytes) await importIntoEngine(source.fileName, source.tableName, source.format === 'xlsx' ? 'csv' : source.format, bytes);
  }
  for (const step of project.transforms.filter((item) => item.enabled)) {
    const { createViewSql } = await import('@numbernode/query-planner');
    const { execute } = await import('../engine/client');
    await execute(createViewSql(step.outputTable, step.sql));
  }
}

export async function loadMostRecentProject(): Promise<NumberNodeProject | undefined> {
  const projects = await listProjects();
  const first = projects[0];
  return first ? loadProject(first.id) : undefined;
}

export async function persistCurrentProject(): Promise<void> {
  await saveProject(useProjectStore.getState().project);
}
